/**
 * Modal di composizione messaggio.
 * - Auto-save bozza ogni N secondi (default 5s)
 * - Allegati upload base64
 * - Smart Reply integrato (quando si risponde a un thread)
 * - Send later (datetime)
 * - Minimizzabile
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Send, Paperclip, Sparkles, Trash2, Clock, Loader2, Minimize2, Maximize2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RecipientInput } from '@/components/compose/RecipientInput';
import { useComposer } from '@/stores/composer.store';
import { useInbox } from '@/stores/inbox.store';
import { accountsApi, draftsApi, aiApi, settingsApi, type DraftAttachment } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function Composer() {
  const open = useComposer((s) => s.open);
  const close = useComposer((s) => s.close);
  const initialDraftId = useComposer((s) => s.draftId);
  const inReplyToMessageId = useComposer((s) => s.inReplyToMessageId);
  const forwardOfMessageId = useComposer((s) => s.forwardOfMessageId);
  const threadId = useComposer((s) => s.threadId);
  const initialTo = useComposer((s) => s.initialTo);
  const initialSubject = useComposer((s) => s.initialSubject);
  const initialBody = useComposer((s) => s.initialBody);

  const qc = useQueryClient();
  const [minimized, setMinimized] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiComposeOpen, setAiComposeOpen] = useState(false);
  const [aiIntent, setAiIntent] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
    enabled: open,
  });
  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    enabled: open,
  });

  // Account mittente: la casella attualmente aperta se può inviare (es. casella
  // condivisa con permesso di invio), altrimenti il default tra quelle inviabili.
  const selectedAccountId = useInbox((s) => s.selectedAccountId);
  const accountId = useMemo(() => {
    const accounts = accountsQuery.data?.accounts ?? [];
    const canSend = (a: { can_send?: boolean }) => a.can_send !== false;
    const current = accounts.find((a) => a.id === selectedAccountId);
    if (current && canSend(current)) return current.id;
    return (
      accounts.find((a) => a.is_default && canSend(a))?.id ??
      accounts.find(canSend)?.id ??
      null
    );
  }, [accountsQuery.data, selectedAccountId]);

  // Mittente effettivo (email) — mostrato nel composer, utile con caselle condivise
  const fromEmail = useMemo(
    () => accountsQuery.data?.accounts.find((a) => a.id === accountId)?.email_address ?? '',
    [accountsQuery.data, accountId]
  );

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Init draft on open
  useEffect(() => {
    if (!open) return;
    setMinimized(false);

    async function init() {
      if (initialDraftId) {
        try {
          const { draft, attachments: atts } = await draftsApi.detail(initialDraftId);
          setDraftId(draft.id);
          setTo(draft.to_addresses.map((a) => a.address).join(', '));
          setCc(draft.cc_addresses.map((a) => a.address).join(', '));
          setShowCc(draft.cc_addresses.length > 0);
          setSubject(draft.subject);
          setBody(draft.body_text);
          setAttachments(atts);
        } catch {
          toast.error('Bozza non caricata');
        }
      } else if (accountId) {
        try {
          // La firma NON va nel riquadro (testo semplice): si aggiunge all'invio, stilizzata.
          // Sotto al corpo viene mostrata un'anteprima della firma.
          const startBody = initialBody ?? '';
          const { draftId: newId } = await draftsApi.create({
            accountId,
            inReplyToMessageId: inReplyToMessageId ?? undefined,
            forwardOfMessageId: forwardOfMessageId ?? undefined,
            threadId: threadId ?? undefined,
            subject: initialSubject,
            to: initialTo,
            bodyText: startBody,
          } as Parameters<typeof draftsApi.create>[0]);
          setDraftId(newId);
          setTo(initialTo.map((a) => a.address).join(', '));
          setSubject(initialSubject);
          setBody(startBody);
          setAttachments([]);
        } catch {
          toast.error('Impossibile creare la bozza');
        }
      } else if (accountsQuery.isSuccess) {
        // Account caricati ma nessuna casella abilitata all'invio (es. solo
        // caselle condivise in sola lettura): non si puo' comporre.
        toast.error('Nessuna casella abilitata all’invio');
        close();
      }
    }
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId, initialDraftId, accountsQuery.isSuccess]);

  function parseAddresses(s: string): { address: string }[] {
    return s
      .split(/[,;]/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((address) => ({ address }));
  }

  // Auto-save
  const triggerAutosave = useCallback(() => {
    if (!draftId || !open) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    const seconds = settingsQuery.data?.settings.auto_save_draft_seconds ?? 5;
    autosaveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await draftsApi.update(draftId, {
          subject,
          body_text: body,
          to_addresses: parseAddresses(to),
          cc_addresses: parseAddresses(cc),
        });
        setSavedAt(new Date());
      } catch { /* silent */ } finally {
        setSaving(false);
      }
    }, seconds * 1000);
  }, [draftId, subject, body, to, cc, open, settingsQuery.data]);

  useEffect(() => {
    triggerAutosave();
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [triggerAutosave]);

  async function handleAttach(files: FileList | null) {
    if (!files || !draftId) return;
    for (const file of Array.from(files)) {
      try {
        const buf = await file.arrayBuffer();
        const base64 = arrayBufferToBase64(buf);
        const result = await draftsApi.uploadAttachment(draftId, {
          filename: file.name,
          contentType: file.type,
          base64,
        });
        setAttachments((a) => [
          ...a,
          {
            id: result.attachmentId,
            filename: file.name,
            content_type: file.type,
            size_bytes: result.sizeBytes,
          },
        ]);
        toast.success(`${file.name} allegato`);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : `Errore upload ${file.name}`);
      }
    }
  }

  async function removeAttachment(id: string) {
    if (!draftId) return;
    try {
      await draftsApi.removeAttachment(draftId, id);
      setAttachments((a) => a.filter((x) => x.id !== id));
    } catch { /* */ }
  }

  async function handleSmartReply() {
    if (!threadId) {
      toast.error('Smart Reply solo rispondendo a un thread');
      return;
    }
    setGeneratingAi(true);
    try {
      const tone = settingsQuery.data?.settings.default_reply_tone ?? 'formale';
      const { body: aiBody } = await aiApi.smartReply({ threadId, tone });
      setBody(aiBody);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Generazione AI fallita');
    } finally {
      setGeneratingAi(false);
    }
  }

  async function handleAiCompose() {
    if (!aiIntent.trim()) return;
    setGeneratingAi(true);
    try {
      const tone = settingsQuery.data?.settings.default_reply_tone ?? 'formale';
      const r = await aiApi.compose({ intent: aiIntent.trim(), tone, to: parseAddresses(to)[0]?.address });
      if (r.subject) setSubject(r.subject);
      setBody(r.body);
      setAiComposeOpen(false);
      setAiIntent('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Generazione AI fallita');
    } finally {
      setGeneratingAi(false);
    }
  }

  const UNDO_MS = 7000;

  async function handleSend(scheduledAt?: string) {
    if (!draftId) return;
    if (parseAddresses(to).length === 0) {
      toast.error('Aggiungi almeno un destinatario');
      return;
    }
    setSending(true);
    try {
      // La firma si aggiunge qui, all'invio. Se è HTML, il messaggio parte come HTML
      // (firma stilizzata + logo visibile al destinatario); altrimenti solo testo.
      const sigText = settingsQuery.data?.settings.signature_text ?? '';
      const sigHtml = settingsQuery.data?.settings.signature_html ?? '';
      // strip difensivo di una firma eventualmente già incollata nel corpo (bozze vecchie/undo)
      let userText = body.replace(/\s+$/, '');
      const st = sigText.trim();
      if (st && userText.endsWith(st)) userText = userText.slice(0, userText.length - st.length).replace(/\s+$/, '');
      const bodyTextToSend = st ? `${userText}\n\n${sigText}` : userText;
      let bodyHtml: string | undefined;
      if (sigHtml) {
        const escaped = userText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
        bodyHtml = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1a1a1a;line-height:1.5">${escaped}</div><br>${sigHtml}`;
      }
      await draftsApi.update(draftId, {
        subject,
        body_text: bodyTextToSend,
        ...(bodyHtml ? { body_html: bodyHtml } : {}),
        to_addresses: parseAddresses(to),
        cc_addresses: parseAddresses(cc),
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Errore salvataggio bozza');
      setSending(false);
      return;
    }
    setSending(false);

    // Invio programmato → invia subito (lo gestisce lo scheduler), nessun undo
    if (scheduledAt) {
      try {
        await draftsApi.send(draftId, scheduledAt);
        qc.invalidateQueries({ queryKey: ['drafts'] });
        toast.success('Invio programmato');
        close();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Errore durante l'invio");
      }
      return;
    }

    // Invio immediato con finestra "Annulla" (undo send, stile Gmail)
    const id = draftId;
    close();
    let cancelled = false;

    // Nasconde subito la bozza dalle Bozze: cosi' non puo' essere riaperta/modificata
    // durante la finestra di annullamento (eviterebbe doppio invio + toast spuri).
    const draftsSnapshot = qc.getQueryData<{ drafts: { id: string }[] }>(['drafts']);
    qc.setQueryData<{ drafts: { id: string }[] } | undefined>(['drafts'], (old) =>
      old?.drafts ? { ...old, drafts: old.drafts.filter((d) => d.id !== id) } : old
    );

    // Se l'utente chiude/ricarica la tab entro la finestra, l'invio (timer in memoria)
    // andrebbe perso silenziosamente: avvisa con il prompt nativo del browser.
    const warnUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warnUnload);
    const cleanup = () => window.removeEventListener('beforeunload', warnUnload);

    const timer = setTimeout(() => {
      cleanup();
      if (cancelled) return;
      draftsApi.send(id)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['drafts'] });
          qc.invalidateQueries({ queryKey: ['messages'] });
        })
        .catch((e) => {
          qc.invalidateQueries({ queryKey: ['drafts'] });
          toast.error(e instanceof ApiError ? e.message : "Errore durante l'invio");
        });
    }, UNDO_MS);

    toast.success('Messaggio inviato', {
      duration: UNDO_MS,
      action: {
        label: 'Annulla',
        onClick: () => {
          cancelled = true;
          clearTimeout(timer);
          cleanup();
          // ripristina la bozza nella lista Bozze
          if (draftsSnapshot) qc.setQueryData(['drafts'], draftsSnapshot);
          qc.invalidateQueries({ queryKey: ['drafts'] });
          toast('Invio annullato — la bozza è salvata');
        },
      },
    });
  }

  async function handleDiscard() {
    if (!draftId) { close(); return; }
    try {
      await draftsApi.remove(draftId);
      qc.invalidateQueries({ queryKey: ['drafts'] });
      toast('Bozza eliminata');
      close();
    } catch { close(); }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-40 flex items-end justify-end p-4 sm:p-6 pointer-events-none"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'pointer-events-auto bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden',
              minimized
                ? 'w-80 h-12'
                : 'w-full sm:w-[640px] h-[90vh] sm:h-[640px] max-h-[90vh]'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
              <div className="flex items-center gap-2 text-sm font-medium">
                {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {!sending && saving && <Loader2 className="h-3.5 w-3.5 animate-spin opacity-50" />}
                <span>
                  {inReplyToMessageId ? 'Rispondi' : forwardOfMessageId ? 'Inoltra' : 'Nuovo messaggio'}
                </span>
                {savedAt && !saving && (
                  <span className="text-xs text-muted-foreground">Salvata</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setMinimized((m) => !m)}>
                  {minimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={close}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {!minimized && (
              <>
                <div className="border-b px-4 py-2 space-y-1.5">
                  {fromEmail && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">Da:</span>
                      <span className="text-xs font-medium truncate">{fromEmail}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-10">A:</span>
                    <RecipientInput
                      value={to}
                      onChange={setTo}
                      placeholder="destinatario@esempio.it"
                      className="h-8 border-0 px-1 focus-visible:ring-0"
                    />
                    {!showCc && (
                      <Button variant="ghost" size="sm" onClick={() => setShowCc(true)} className="h-7 px-2 text-xs">
                        Cc
                      </Button>
                    )}
                  </div>
                  {showCc && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">Cc:</span>
                      <RecipientInput
                        value={cc}
                        onChange={setCc}
                        placeholder="cc@esempio.it"
                        className="h-8 border-0 px-1 focus-visible:ring-0"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-10">Ogg:</span>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Oggetto"
                      className="h-8 border-0 px-1 focus-visible:ring-0 font-medium"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Scrivi qui..."
                    className="w-full min-h-[160px] border-0 resize-none focus-visible:ring-0 p-0 text-sm leading-relaxed"
                  />

                  {(settingsQuery.data?.settings.signature_html || settingsQuery.data?.settings.signature_text) && (
                    <div className="mt-4 border-t pt-3">
                      <div className="text-[11px] text-muted-foreground mb-1.5">Firma (aggiunta automaticamente all’invio)</div>
                      {settingsQuery.data?.settings.signature_html ? (
                        <div className="text-sm" dangerouslySetInnerHTML={{ __html: settingsQuery.data.settings.signature_html }} />
                      ) : (
                        <pre className="text-sm whitespace-pre-wrap font-sans text-muted-foreground m-0">{settingsQuery.data?.settings.signature_text}</pre>
                      )}
                    </div>
                  )}

                  {attachments.length > 0 && (
                    <div className="mt-4 space-y-1.5">
                      {attachments.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-sm bg-muted/40 rounded px-2 py-1.5">
                          <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="flex-1 truncate">{a.filename}</span>
                          <span className="text-xs text-muted-foreground">{formatBytes(a.size_bytes)}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => void removeAttachment(a.id)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t px-3 py-2 flex items-center gap-1 flex-wrap">
                  <Button
                    onClick={() => void handleSend()}
                    disabled={sending || !draftId}
                    size="sm"
                    className="gap-1.5"
                  >
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Invia
                  </Button>

                  <label className="cursor-pointer">
                    <input type="file" multiple className="hidden"
                      onChange={(e) => void handleAttach(e.target.files)} />
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent">
                      <Paperclip className="h-4 w-4" />
                    </span>
                  </label>

                  {threadId ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => void handleSmartReply()}
                      disabled={generatingAi}
                      title="Smart Reply (AI)"
                    >
                      {generatingAi ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      ) : (
                        <Sparkles className="h-4 w-4 text-primary" />
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAiComposeOpen((v) => !v)}
                      disabled={generatingAi}
                      title="Scrivi con AI"
                      className="gap-1.5"
                    >
                      {generatingAi ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Sparkles className="h-4 w-4 text-primary" />}
                      <span className="text-xs">Scrivi con AI</span>
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setScheduling((s) => !s)}
                    title="Programma invio"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>

                  <div className="ml-auto">
                    <Button variant="ghost" size="icon" onClick={() => void handleDiscard()} title="Elimina bozza">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {aiComposeOpen && !threadId && (
                    <div className="basis-full mt-2 pt-2 border-t space-y-2">
                      <textarea
                        value={aiIntent}
                        onChange={(e) => setAiIntent(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="Cosa vuoi scrivere? Es: chiedi un preventivo a un fornitore per 100 bomboniere, consegna entro fine mese"
                        className="w-full text-sm rounded-md border bg-background px-3 py-2 outline-none focus:ring-1 focus:ring-primary leading-relaxed"
                      />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => void handleAiCompose()} disabled={!aiIntent.trim() || generatingAi}>
                          {generatingAi ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                          Genera email
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAiComposeOpen(false)}>Annulla</Button>
                      </div>
                    </div>
                  )}

                  {scheduling && (
                    <div className="basis-full flex items-center gap-2 mt-2 pt-2 border-t">
                      <Input
                        type="datetime-local"
                        value={scheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                        className="h-8 text-xs"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          if (!scheduleAt) return;
                          void handleSend(new Date(scheduleAt).toISOString());
                        }}
                        disabled={!scheduleAt}
                      >
                        Programma
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  return btoa(binary);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
